import {
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl} from '@angular/forms';
import {BaseComponent} from '../base.component';
import {Observable, of, Subject} from 'rxjs';
import {debounceTime, map, switchMap, tap} from 'rxjs/operators';
import {IEmployeeSearchResult, IUserLookupItemModel} from '../../../../../DBadgeEmployee-app/src/app/models/data.models';

type ILookupOption = any; // Use abstract type, to mark lookup option with some type (alias)

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => LookupComponent), // tslint:disable-line
    multi: true
};

const noop = () => {
    // empty
};

@Component({
    selector: 'ui-lookup',
    templateUrl: './lookup.component.html',
    styleUrls: ['./lookup.component.less'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class LookupComponent extends BaseComponent implements ControlValueAccessor, OnInit {

    constructor(private elementRef: ElementRef
    ) {
        super();
    }

    get showSpinner(): boolean {
        return this.searchInProgress;
    }

    get showClearControl(): boolean {
        return this.lookupQuery &&
            (typeof this.lookupQuery === 'string' ? !!this.lookupQuery.trim().length : !!this.lookupQuery)
            && !this.searchInProgress
            && !this.isDisabled;
    }

    get showResults(): boolean {
        return !!this.inputControl && this.inputControl.dirty
            && this.lookupQuery
            && this.lookupQuery.trim().length >= this.minQueryLength
            && this.isOpened;
    }

    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    private readonly queryChangesSource$ = new Subject<string>();

    /**
     * 'lookupOption' can be primitive or object type.
     * In case if input type is an object then we need to use 'propertyNameToDisplay' to convert option to displayed text.
     * Else, option would be displayed in search input by default.
     */
    @Input() propertyNameToDisplay: string;
    @Input() placeholderText = 'Search by name, e-mail or alias';
    // Lookup component requires source to perform search.
    @Input() private dataSource: (searchQuery: string, siteId?: number) => Observable<Array<ILookupOption>>;
    @Input() dataField: string;
    @Input() maxQueryLength: number = 250;
    @Input() private initialFocus = false;
    @Input() set value(option: any) {
        if (!option) {
            return;
        }

        this.lookupQuery = option[this.dataField];
    }
    @Input() set disabled(value: boolean) {
        if (typeof value !== 'boolean') {
            return;
        }
        this.setDisabledState(value);
    }
    @Input() srollItemHeight: number = 48; // default suggestion item heigth with only text iside
    @Input() quantityOfVisibleItems: number = 6;
    @Input() clearOnInput = false;

    @Output() public onOptionSelect: EventEmitter<ILookupOption> = new EventEmitter(); // tslint:disable-line
    @Output() public onClickOutside: EventEmitter<ILookupOption> = new EventEmitter(); // tslint:disable-line
    @Output() public onSelectData: EventEmitter<IUserLookupItemModel | IEmployeeSearchResult> = new EventEmitter(); // tslint:disable-line
    @Output() onBlur = new EventEmitter();
    @Output() onQuery: EventEmitter<string> = new EventEmitter();

    @ViewChild('inputControl', {}) private inputControl: UntypedFormControl;
    @ContentChild(TemplateRef, {}) lookupOption: TemplateRef<any>;

    optionsList: Array<ILookupOption> = null;
    lookupQuery = '';
    private innerSelectedOption: ILookupOption = null;

    private get selectedOption(): ILookupOption {
        return this.innerSelectedOption;
    }

    private set selectedOption(option: ILookupOption) {
        this.innerSelectedOption = option;
        // serialize selected value to emit as string value to outside
        if (this.dataField && this.innerSelectedOption) {
            this.onChangeCallback(this.innerSelectedOption[this.dataField]);
            this.onOptionSelect.emit(this.innerSelectedOption[this.dataField]);
            this.onSelectData.emit(this.innerSelectedOption);
        } else {
            const serializedSelectedOption = this.innerSelectedOption == null
                ? null
                : JSON.stringify(this.innerSelectedOption);
            this.onChangeCallback(serializedSelectedOption);
            this.onOptionSelect.emit(serializedSelectedOption);
            this.onSelectData.emit(this.innerSelectedOption);
        }
        if (this.clearOnInput) {
            this.innerSelectedOption = null;
        }
    }

    private isOpened = false;
    private searchInProgress = false;
    private readonly minQueryLength: number = 3;
    isDisabled = false;

    @HostListener('document:click', ['$event.target'])
    clickOutside(target: HTMLElement): void {
        const clickedInside: boolean = this.elementRef.nativeElement.contains(target);
        if (!clickedInside && this.isOpened) {
            this.close();
            this.lookupQuery = this.lookupQuery.trim();
            this.onClickOutside.emit(true);
            this.onBlur.emit(this.lookupQuery);
        }
    }

    // FORM CONTROL IMPLEMENTATION START
    writeValue(value: string): void {
        this.lookupQuery = value ? value.trim() : '';
        this.optionsList = null;
        this.close();
    }

    registerOnChange(fn: any): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this.close();
    }

    // FORM CONTROL IMPLEMENTATION END

    ngOnInit(): void {
        this.trackInputValueChanges();
        if (this.initialFocus) {
            (this.elementRef.nativeElement as HTMLElement).querySelector('input').focus();
        }
    }

    onInput(e: KeyboardEvent): void {
        const enteredSymbolPosition = (e.target as HTMLInputElement).selectionStart;
        if (
            e.key === ' ' && enteredSymbolPosition === 0
            || this.lookupQuery.charAt(enteredSymbolPosition - 1) === ' '
            || this.lookupQuery.charAt(enteredSymbolPosition) === ' ') {
            e.preventDefault();
        }
    }

    onQueryChange(query: string): void {
        if (this.queryChangesSource$.observers.length === 0) {
            this.trackInputValueChanges();
        }

        this.queryChangesSource$.next(query);
        this.onQuery.emit(query);
    }

    resetSearchResults(): void {
        this.lookupQuery = '';
        this.selectedOption = null;
        this.onClickOutside.emit(false);
        this.onQuery.emit('');
    }

    open(): void {
        this.isOpened = !!this.optionsList;
        this.onTouchedCallback();
    }

    close(): void {
        this.isOpened = false;
    }

    select(selectedOption: ILookupOption): void {
        this.close();
        this.selectedOption = selectedOption;
        this.lookupQuery = this.propertyNameToDisplay && this.innerSelectedOption?.hasOwnProperty(this.propertyNameToDisplay)
            ? this.innerSelectedOption[this.propertyNameToDisplay]
            : this.innerSelectedOption;
    }

    private trackInputValueChanges(): void {
        this.subs = this.queryChangesSource$.pipe(
            map((query: string) => query.trim()),
            tap((query: string) => {
                this.close();
                this.optionsList = null;
                this.selectedOption = null;
            }),
            debounceTime(400),
            tap(() => this.searchInProgress = true),
            switchMap((query: string) => {
                const shouldSendRequest: boolean = (query.length + 1 > this.minQueryLength)
                    && this.inputControl.dirty
                    && !this.inputControl.disabled;
                return shouldSendRequest ? this.dataSource(query) : of([]);
            })).subscribe((result: Array<ILookupOption>) => {
            this.searchInProgress = false;
            this.optionsList = result;
            this.open(); // show results after search
        }, (error) => {
            this.searchInProgress = false;
        });
    }

}
